import type { DefinicaoDeFluxo, OfertaDeTransicao } from '@/features/workflow/tipos';

/**
 * As decisões da interface de fluxo que não dependem de React.
 *
 * Moram aqui, e não dentro dos componentes, porque são exatamente as que valem
 * a pena testar: quais versões podem receber um chamado, quando a transição
 * precisa de formulário, se o texto do editor é um grafo. Cada uma tem uma
 * resposta certa e nenhuma delas precisa de DOM para ser verificada.
 */

export interface OpcaoDeFluxo {
  versionId: string;
  nome: string;
  numero: number;
}

/**
 * Só o que aceita vínculo novo: definição ativa, do tipo de entidade pedido,
 * na versão publicada.
 *
 * Rascunho e aposentada ficam de fora porque o servidor as recusa com 409 —
 * `canBindNew` só aceita `PUBLISHED`. Oferecer uma opção que sempre falha é
 * pior do que não oferecer nenhuma. A filtragem por `entityType` é da mesma
 * natureza: um fluxo de projeto num chamado não teria sequer contexto de
 * expressão para avaliar os guards.
 */
export function versoesPublicadas(
  definicoes: readonly DefinicaoDeFluxo[],
  entityType = 'ticket',
): OpcaoDeFluxo[] {
  return definicoes
    .filter((definicao) => definicao.active && definicao.entityType === entityType)
    .flatMap((definicao) =>
      definicao.versions
        .filter((versao) => versao.status === 'PUBLISHED')
        .map((versao) => ({
          versionId: versao.id,
          nome: definicao.name,
          numero: versao.versionNumber,
        })),
    );
}

/**
 * A transição abre formulário, ou vai direto?
 *
 * Campos obrigatórios e justificativa **não** barram o botão — o motor é
 * explícito quanto a isso: eles vêm de um formulário que ainda nem foi aberto.
 * O que eles decidem é se há formulário a abrir.
 */
export const precisaDeFormulario = (oferta: OfertaDeTransicao): boolean =>
  oferta.requiredFields.length > 0 || oferta.requiresReason;

export interface DefinicaoLida {
  graph: Record<string, unknown> | null;
  erro: string | null;
}

/**
 * Lê o texto do editor como grafo.
 *
 * A análise acontece na tela, e não só no servidor, porque o `class-validator`
 * recusaria com "graph must be an object" — que não diz onde está a vírgula
 * sobrando. A mensagem do `JSON.parse` diz.
 *
 * Texto vazio devolve os dois campos nulos: não é erro, é o estado inicial do
 * campo, e pintá-lo de vermelho antes de a pessoa digitar seria ruído.
 */
export function lerDefinicao(texto: string): DefinicaoLida {
  if (texto.trim() === '') return { graph: null, erro: null };
  try {
    const valor: unknown = JSON.parse(texto);
    if (valor === null || typeof valor !== 'object' || Array.isArray(valor)) {
      return { graph: null, erro: 'a definição precisa ser um objeto.' };
    }
    return { graph: valor as Record<string, unknown>, erro: null };
  } catch (erro) {
    return { graph: null, erro: erro instanceof Error ? erro.message : 'não foi possível ler.' };
  }
}

/**
 * A chave sugerida a partir do nome, no formato que o servidor aceita.
 *
 * O mesmo `FORMATO_DE_CHAVE` do DTO e do CHECK no banco: minúsculas, dígitos,
 * ponto, hífen e sublinhado, começando por letra ou dígito. Sugerir evita que
 * a mesma ideia vire `Mudanca`, `mudanca_2` e `mudanca-nova` em três fluxos.
 */
export function chaveSugerida(nome: string): string {
  return nome
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^[^a-z0-9]+|-+$/g, '')
    .slice(0, 64);
}

/** O próximo número de versão, para a tela dizer o que o "salvar" vai criar. */
export const proximoNumeroDeVersao = (definicao: DefinicaoDeFluxo): number =>
  Math.max(0, ...definicao.versions.map((versao) => versao.versionNumber)) + 1;
